import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { SystemMessageAudience, SystemMessageDtoIn } from '@/models/in/SystemMessageDtoIn';
import { FeatureFlagDtoIn } from '@/models/in/FeatureFlagDtoIn';
import { FeatureLifecycleStatus } from '@/models/common/FeatureLifecycleStatus';
import DataTable from '@/components/ui/DataTable/DataTable';
import { Col } from '@/components/ui/DataTable/ColumnsHelpers';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import Button from '@/components/ui/Button';
import StatusFilter from '@/components/filters/StatusFilter/StatusFilter';
import StateChip from '@/components/ui/StateChip/StateChip';
import HoverHint from '@/components/ui/Tooltip/HoverHint';
import Alert from '@/components/ui/Alert';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { putAuth } from '@/api/ApiCalls';
import filterStyles from '@/components/filters/FiltersArea.module.scss';
import useTableDensity from '@/hooks/useTableDensity';
import { familyClassForType, iconForMessage } from '@/components/SystemMessages/systemMessageIcons';
import EditOrCreateSystemMessageDialog from './EditOrCreateSystemMessageDialog';
import DeleteSystemMessageDialog from './DeleteSystemMessageDialog';
import { formatDate, isLiveNow, SYSTEM_MESSAGES_MANAGEMENT_URL } from './systemMessages';
import { releaseReturnUrl } from '@/pages/ManagementArea/FeatureFlags/releaseDraft';
import styles from './SystemMessagesManagementPage.module.scss';

const DENSITY_STORAGE_KEY = 'table.density.systemMessages';

/** The chips above the table: whom a message is sent to (HAT-1489). */
const AUDIENCE_FILTERS: SystemMessageAudience[] = ['all', 'coordinators', 'office'];

/** Three states, not five: shown, waiting for its feature, not shown (why, on hover). */
type MessageState = 'shown' | 'waiting' | 'off';
const STATE_FILTERS: MessageState[] = ['shown', 'waiting', 'off'];
const STATE_DOT: Record<MessageState, string> = { shown: 'var(--status-approved-border)', waiting: 'var(--status-pending-border)', off: 'var(--border-low)' };
const STATE_RANK: Record<MessageState, number> = { shown: 0, waiting: 1, off: 2 };

/**
 * «הודעות למשתמשים» (HAT-1489 rework): one line per message, whom it goes to and about which
 * feature, when it is valid, and one of three states. A row opens the editor; the bin is the only
 * other action, beside «לשחרור» on a message still waiting for its feature.
 */
const SystemMessagesManagementPage = () => {
  const { t } = useTranslation();

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [messageToEdit, setMessageToEdit] = useState<SystemMessageDtoIn | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<SystemMessageDtoIn | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [density, setDensity] = useTableDensity(DENSITY_STORAGE_KEY, 'normal');
  const [audienceFilter, setAudienceFilter] = useState<SystemMessageAudience | null>(null);
  const [stateFilter, setStateFilter] = useState<MessageState | null>(null);

  const { data: messages, isLoading, refetch } = useGetAuthApi<SystemMessageDtoIn[]>(SYSTEM_MESSAGES_MANAGEMENT_URL);

  /*
   * A feature's announcement is live when its flag is, so its state is read off the flag: waiting
   * for the release while the flag is closed, shown to whoever has the feature after.
   */
  const { data: flags } = useGetAuthApi<FeatureFlagDtoIn[]>('management/feature-flags');
  const flagByKey = useMemo(() => new Map((flags ?? []).map((flag) => [flag.featureKey, flag])), [flags]);
  const isReleased = (flag: FeatureFlagDtoIn | undefined) =>
    !!flag && (!!flag.finalizedAt || flag.lifecycleStatus !== FeatureLifecycleStatus.NotReleased);

  const stateOf = useCallback((message: SystemMessageDtoIn): MessageState => {
    if (!isLiveNow(message)) return 'off';
    if (message.featureFlagKey && !isReleased(flagByKey.get(message.featureFlagKey))) return 'waiting';
    return 'shown';
  }, [flagByKey]);

  /*
   * The row's switch: shows a message or takes it down, and nothing else - the server leaves the
   * message as it was, so whoever already read it is not told it again.
   */
  const { currentUser } = useAuthContext();
  const [switching, setSwitching] = useState<number | null>(null);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const handleSetActive = async (message: SystemMessageDtoIn, isActive: boolean) => {
    setSwitching(message.id);
    setSwitchError(null);
    try {
      await putAuth<SystemMessageDtoIn, object, { isActive: boolean }>(
        `${SYSTEM_MESSAGES_MANAGEMENT_URL}/${message.id}/active`,
        { Authorization: `Bearer ${currentUser?.token}` },
        {},
        { isActive },
      );
      await refetch();
    } catch {
      setSwitchError(t('systemMessages.switchFailed', { title: message.title }));
    } finally {
      setSwitching(null);
    }
  };

  const audienceCounts = useMemo(() => {
    const counts: Record<SystemMessageAudience, number> = { all: 0, coordinators: 0, office: 0 };
    (messages ?? []).forEach((message) => { counts[message.audience] += 1; });
    return counts;
  }, [messages]);
  const stateCounts = useMemo(() => {
    const counts: Record<MessageState, number> = { shown: 0, waiting: 0, off: 0 };
    (messages ?? []).forEach((message) => { counts[stateOf(message)] += 1; });
    return counts;
  }, [messages, stateOf]);

  const newsMoment = (message: SystemMessageDtoIn) => message.publishedAt ?? message.updatedAt ?? message.createdAt ?? '';
  /*
   * Shown first, then waiting, then off - as the page opens. A row keeps its place after that: the
   * switch in it changes its state, and a row that leapt to the bottom under the pointer would read as
   * gone. A message added since is placed after the rest.
   */
  const placeById = useRef(new Map<number, number>());
  const shownMessages = useMemo(() => {
    const byState = (a: SystemMessageDtoIn, b: SystemMessageDtoIn) =>
      STATE_RANK[stateOf(a)] - STATE_RANK[stateOf(b)] || newsMoment(b).localeCompare(newsMoment(a));
    const places = placeById.current;
    // Places are fixed only once the flags are in: until then every feature's message reads as waiting.
    if (flags) [...(messages ?? [])].sort(byState).forEach((message) => { if (!places.has(message.id)) places.set(message.id, places.size); });
    return (messages ?? [])
      .filter((message) => !audienceFilter || message.audience === audienceFilter)
      .filter((message) => !stateFilter || stateOf(message) === stateFilter)
      .sort((a, b) => (flags ? (places.get(a.id) ?? 0) - (places.get(b.id) ?? 0) : byState(a, b)));
  }, [messages, flags, audienceFilter, stateFilter, stateOf]);

  // A new message for a feature, written from its release: linked to that flag.
  const [linkTo, setLinkTo] = useState<{ featureFlagKey: string; audience: SystemMessageAudience } | null>(null);

  const handleOpenCreateDialog = useCallback(() => {
    setMessageToEdit(null);
    setLinkTo(null);
    setIsEditDialogOpen(true);
  }, []);

  const handleOpenEditDialog = useCallback((message: SystemMessageDtoIn) => {
    setMessageToEdit(message);
    setLinkTo(null);
    setIsEditDialogOpen(true);
  }, []);

  /*
   * A feature's release sends its «עריכה» and «+ הוספת הודעה» here (HAT-1489): the wording is edited
   * on this page, not in a window over the release. ?message=<id> opens that message, ?newFor=<flag>
   * a new one linked to the flag. The parameter is dropped once used, so a reload does not reopen it.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const wantedId = Number(searchParams.get('message')) || null;
  const wantedNewFor = searchParams.get('newFor');
  // ?returnTo=<flag>: opened from that feature's release, and closing or saving goes back to it.
  const [returnTo, setReturnTo] = useState<string | null>(null);
  useEffect(() => {
    if (wantedNewFor) {
      setMessageToEdit(null);
      setLinkTo({ featureFlagKey: wantedNewFor, audience: (searchParams.get('audience') as SystemMessageAudience) || 'all' });
      setIsEditDialogOpen(true);
    } else if (wantedId) {
      const message = (messages ?? []).find((m) => m.id === wantedId);
      if (!message) return;
      setMessageToEdit(message);
      setLinkTo(null);
      setIsEditDialogOpen(true);
    } else {
      return;
    }
    // Read with the message it belongs to, and kept after the parameters are dropped below.
    setReturnTo(searchParams.get('returnTo'));
    const next = new URLSearchParams(searchParams);
    ['message', 'newFor', 'audience', 'returnTo'].forEach((key) => next.delete(key));
    setSearchParams(next, { replace: true });
  }, [wantedId, wantedNewFor, messages, searchParams, setSearchParams]);

  const handleCloseEditDialog = useCallback(() => {
    setIsEditDialogOpen(false);
    setMessageToEdit(null);
    setLinkTo(null);
    if (returnTo) {
      setReturnTo(null);
      navigate(releaseReturnUrl(returnTo));
    }
  }, [returnTo, navigate]);

  const handleOpenDeleteDialog = useCallback((message: SystemMessageDtoIn) => {
    setMessageToDelete(message);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setIsDeleteDialogOpen(false);
    setMessageToDelete(null);
  }, []);

  const validity = (message: SystemMessageDtoIn) => {
    const from = formatDate(message.validFrom);
    const to = formatDate(message.validTo);
    if (!from && !to) return t('systemMessages.noLimit');
    if (from && to) return `${from}–${to}`;
    return from ? t('systemMessages.validity.from', { date: from }) : t('systemMessages.validity.to', { date: to });
  };

  const columns: ColumnDef<SystemMessageDtoIn>[] = [
    {
      id: 'title',
      header: Col(t('systemMessages.title')),
      accessorKey: 'title',
      enableSorting: true,
      // The glyph and the title, one line; the lead is on hover. A row reads like the message it
      // stands for rather than like a database record.
      cell: ({ row }) => {
        const message = row.original;
        return (
          <HoverHint as="div" className={`${styles.messageCell} ${styles[familyClassForType[message.type] ?? 'blue']}`} hint={message.body ?? ''}>
            <span className={styles.rowIcon}>
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
                {iconForMessage(message)}
              </svg>
            </span>
            <span className={styles.rowTitle}>{message.title}</span>
          </HoverHint>
        );
      },
      meta: { className: 'min-w-[260px]' },
    },
    {
      /*
       * Whom the message goes to, and - for a feature's announcement - which feature, as a link to
       * its release. Two separate facts: the office's message about a coordinators' feature is sent
       * to the office, and the row has to say both without either reading as the other.
       */
      id: 'audience',
      header: Col(t('systemMessages.audience')),
      accessorKey: 'audience',
      enableSorting: true,
      cell: ({ row }) => {
        const message = row.original;
        const flag = message.featureFlagKey ? flagByKey.get(message.featureFlagKey) : undefined;
        return (
          <div className={styles.audienceCell}>
            <StateChip tone={message.audience === 'coordinators' ? 'amber' : 'grey'}>{t(`announcements.audience.${message.audience}`)}</StateChip>
            {message.featureFlagKey && (
              <span className={styles.audienceFeature}>
                {t('systemMessages.aboutFeature')}{' '}
                <Link
                  to={`/management/feature-flags?flag=${encodeURIComponent(message.featureFlagKey)}`}
                  onClick={(e) => e.stopPropagation()}
                  className={styles.featureLink}
                >
                  {flag?.displayName ?? message.featureFlagKey}
                </Link>
              </span>
            )}
          </div>
        );
      },
      meta: { className: 'min-w-[170px] max-w-[260px]' },
    },
    {
      id: 'validity',
      header: Col(t('systemMessages.validity.title')),
      accessorKey: 'validFrom',
      enableSorting: true,
      cell: ({ row }) => <span>{validity(row.original)}</span>,
      meta: { className: 'min-w-[130px] max-w-[170px]' },
    },
    {
      id: 'status',
      header: Col(t('systemMessages.status')),
      enableSorting: false,
      cell: ({ row }) => {
        const message = row.original;
        const state = stateOf(message);
        const chip = state === 'shown'
          ? <StateChip tone="green" dot>{t('systemMessages.state.shown')}</StateChip>
          : state === 'waiting'
            ? <StateChip tone="amber" dot>{t('systemMessages.state.waiting')}</StateChip>
            : <StateChip tone="grey" dot>{t('systemMessages.state.off')}</StateChip>;
        // Off only because its dates are past or still ahead: the switch cannot help, the dates can.
        if (message.isActive && !isLiveNow(message)) {
          return <HoverHint hint={t('systemMessages.reason.outOfWindow')}>{chip}</HoverHint>;
        }
        /*
         * The state is the switch: pressing it takes the message down or puts it back, without opening
         * it. What that means now is on hover, and in the chip once it changes.
         */
        const hint = state === 'shown'
          ? t('systemMessages.switch.hide')
          : state === 'waiting'
            ? `${t('systemMessages.reason.waiting')}. ${t('systemMessages.switch.hide')}`
            : t('systemMessages.switch.show');
        return (
          <HoverHint hint={hint}>
            <button
              type="button"
              className={styles.stateButton}
              aria-pressed={message.isActive}
              aria-label={`${t(`systemMessages.state.${state}`)}. ${hint}`}
              disabled={switching === message.id}
              onClick={(e) => {
                e.stopPropagation();
                void handleSetActive(message, !message.isActive);
              }}
            >
              {chip}
            </button>
          </HoverHint>
        );
      },
      meta: { className: 'min-w-[150px] max-w-[190px]' },
    },
    {
      id: 'actions',
      header: Col(''),
      enableSorting: false,
      cell: ({ row }) => {
        const message = row.original;
        return (
          <div className={styles.actions} onClick={(e) => e.stopPropagation()}>
            {stateOf(message) === 'waiting' && message.featureFlagKey && (
              <Link to={`/management/feature-flags?flag=${encodeURIComponent(message.featureFlagKey)}`} className={`${styles.releaseLink} body-sm-semibold`}>
                {t('systemMessages.toRelease')}
              </Link>
            )}
            <HoverHint hint={t('systemMessages.delete')}>
              <button type="button" className={styles.binButton} onClick={() => handleOpenDeleteDialog(message)} aria-label={t('systemMessages.delete')}>
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
                  <path d="M4.5 7h15M9.5 7V4.5h5V7M6.5 7l1 12.5h9L17.5 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </HoverHint>
          </div>
        );
      },
      meta: { className: 'min-w-[120px] w-[120px]', lockLayout: true },
    },
  ];

  return (
    <div>
      <DataTable
        title={t('systemMessages.pageTitle')}
        data={shownMessages}
        isLoading={isLoading}
        columns={columns}
        filters={
          <div className="flex items-end gap-3 max-[767px]:flex-col max-[767px]:items-stretch">
            <div className="flex-grow">
              <div className={filterStyles.filtersArea}>
                <div className={filterStyles.filtersContainer} data-board-filters>
                  {/* Every message, whoever it is sent to - the default, and the way back from a filter. */}
                  <StatusFilter
                    title="systemMessages.audienceFilter.any"
                    count={messages?.length ?? 0}
                    isSelected={audienceFilter === null && stateFilter === null}
                    onClick={() => { setAudienceFilter(null); setStateFilter(null); }}
                  />
                  {AUDIENCE_FILTERS.map((audience) => (
                    <StatusFilter
                      key={audience}
                      title={`announcements.audience.${audience}`}
                      count={audienceCounts[audience]}
                      isSelected={audienceFilter === audience}
                      disabled={audienceCounts[audience] === 0}
                      onClick={() => setAudienceFilter(audienceFilter === audience ? null : audience)}
                    />
                  ))}
                  <span className={styles.divider} aria-hidden="true" />
                  {STATE_FILTERS.map((state) => (
                    <StatusFilter
                      key={state}
                      title={`systemMessages.stateFilter.${state}`}
                      count={stateCounts[state]}
                      iconColor={STATE_DOT[state]}
                      isSelected={stateFilter === state}
                      disabled={stateCounts[state] === 0}
                      onClick={() => setStateFilter(stateFilter === state ? null : state)}
                    />
                  ))}
                </div>
              </div>
            </div>
            <Button variant="primary" onClick={handleOpenCreateDialog} className="whitespace-nowrap shrink-0">
              {t('systemMessages.addMessage')}
            </Button>
          </div>
        }
        notice={switchError ? <Alert type="error" message={switchError} onClose={() => setSwitchError(null)} /> : undefined}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        density={density}
        onDensityChange={setDensity}
        handleRowClick={handleOpenEditDialog}
        columnLayoutKey="systemMessages.v2"
        twoLineRows
        searchPlaceholder="systemMessages.search"
        customGlobalSearch={() => true}
      />

      <EditOrCreateSystemMessageDialog open={isEditDialogOpen} onClose={handleCloseEditDialog} messageToEdit={messageToEdit} linkTo={linkTo} returnsToRelease={!!returnTo} onSuccess={refetch} />

      {isDeleteDialogOpen && messageToDelete && (
        <DeleteSystemMessageDialog open={isDeleteDialogOpen} onClose={handleCloseDeleteDialog} message={messageToDelete} onSuccess={refetch} />
      )}
    </div>
  );
};

export default SystemMessagesManagementPage;
